/**
 * Calling View Component
 * Displays waiting state while waiting for remote user to join
 * Responsive: compact mode for mini view
 */

import React, { useCallback, useEffect, useState } from "react";
import { Phone, Loader2, Bell } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  videoCallMutedOverlayClassName,
  videoCallPanelClassName,
  videoCallSurfaceClassName,
} from "./video-call-theme";

const NOTIFY_COOLDOWN_SECONDS = 60;

interface CallingViewProps {
  patientName?: string;
  waitingTime?: string;
  className?: string;
  compact?: boolean;
  onNotifyPatient?: () => void;
}

export const CallingView: React.FC<CallingViewProps> = ({
  patientName = "Patient",
  waitingTime = "00:00",
  className,
  compact = false,
  onNotifyPatient,
}) => {
  const [dots, setDots] = useState("");
  const [notifyCooldown, setNotifyCooldown] = useState(0);

  // Animate dots
  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? "" : prev + "."));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Notify cooldown countdown
  useEffect(() => {
    if (notifyCooldown <= 0) return;
    const interval = setInterval(() => {
      setNotifyCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [notifyCooldown]);

  const handleNotifyPatient = useCallback(() => {
    if (notifyCooldown > 0) return;
    onNotifyPatient?.();
    setNotifyCooldown(NOTIFY_COOLDOWN_SECONDS);
  }, [notifyCooldown, onNotifyPatient]);

  if (compact) {
    return (
      <div
        className={cn(
          "flex h-full w-full flex-col items-center justify-center gap-2 p-2 text-center",
          videoCallSurfaceClassName,
          className
        )}
      >
        <div className="relative">
          <div className="absolute inset-0 animate-ping rounded-full h-12 w-12 border-2 border-primary/30" />
          <div className="relative flex items-center justify-center h-12 w-12">
            <div
              className={cn(
                "flex h-10 w-10 items-center justify-center rounded-full border border-border",
                videoCallMutedOverlayClassName
              )}
            >
              <Phone className="h-4 w-4 text-primary animate-pulse" />
            </div>
          </div>
        </div>
        <p className="max-w-full truncate text-xs font-medium text-foreground">
          Waiting for {patientName}
          {dots}
        </p>
        <span className="text-xs text-muted-foreground">{waitingTime}</span>
        {onNotifyPatient && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleNotifyPatient}
            disabled={notifyCooldown > 0}
            className="mt-1 h-7 rounded-lg text-xs gap-1.5"
          >
            <Bell className="size-3" />
            {notifyCooldown > 0
              ? `Retry in ${notifyCooldown}s`
              : "Notify Patient"}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex h-full w-full flex-col items-center justify-center rounded-lg p-4 text-center md:p-6",
        videoCallSurfaceClassName,
        className
      )}
    >
      <div className="relative mb-4 md:mb-6">
        <div className="absolute inset-0 h-16 w-16 animate-ping rounded-full border-2 border-primary/30 md:h-24 md:w-24 md:border-4" />
        <div className="absolute inset-0 h-16 w-16 animate-pulse rounded-full border border-primary/50 md:h-24 md:w-24 md:border-2" />
        <div className="relative flex h-16 w-16 items-center justify-center md:h-24 md:w-24">
          <div
            className={cn(
              "flex h-12 w-12 items-center justify-center rounded-full border border-border md:h-20 md:w-20",
              videoCallMutedOverlayClassName
            )}
          >
            <Phone className="h-5 w-5 animate-pulse text-primary md:h-8 md:w-8" />
          </div>
        </div>
      </div>

      <div className={cn("w-full max-w-sm space-y-2", videoCallPanelClassName)}>
        <div className="space-y-1 p-4 md:p-5">
          <h3 className="text-sm font-semibold text-foreground md:text-lg">
            Waiting for {patientName}
            {dots}
          </h3>
          <p className="text-xs text-muted-foreground md:text-sm">
            Patient will join shortly
          </p>
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin md:h-4 md:w-4" />
            <span className="text-xs md:text-sm">{waitingTime}</span>
          </div>
          {onNotifyPatient && (
            <div className="pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleNotifyPatient}
                disabled={notifyCooldown > 0}
                className="rounded-xl text-xs gap-2 md:text-sm"
              >
                <Bell className="size-3.5" />
                {notifyCooldown > 0
                  ? `Retry in ${notifyCooldown}s`
                  : "Notify Patient"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CallingView;
